// Billing in the person's words (02-DESIGN/18-billing/02-the-billing-page.md
// § the tab, top to bottom, § the words, § Home, § refusals, in the page's
// words; 02-DESIGN/03-console/01-the-console.md § Settings: the Billing tab;
// decision 0073 § 11): the tab's line in each state, the amounts in euros
// with the cents and the VAT named by its rate, what a plan grants and what
// an add-on unit adds in the registry's words for people, the bar, the
// details' VAT answer, a receipt's status and amount, every refusal in one
// sentence, and Home's line. Pure: the Billing tab, Home and a receipt's page
// draw on it.
//
// The words: *plan*, *add-on*, *a month*, *charged now*, *from <date>*,
// *receipt* for what the page lists and *invoice* for what Impire sends,
// *payment method*, *trial*, *past due* only inside *your payment … failed*.

import { plural } from "../../ui/components.tsx";
import { Limit } from "../../wire/auth.ts";
import {
  ChargeStatus,
  Code,
  Direction,
  State,
  Treatment,
  type AddOn,
  type Amount,
  type Catalogue,
  type Change,
  type Charge,
  type Details,
  type DetailsPutRequest,
  type Line,
  type Plan,
  type PreviewReply,
  type Subscription,
  type SubscriptionReply,
} from "../../wire/billing.ts";
import { dayOf, daysLeft, FEATURE_WORDS } from "./plan.ts";

/** codeOf is a refusal's code, or nothing. */
function codeOf(err: unknown): string | undefined {
  return typeof err === "object" && err !== null && "code" in err && typeof err.code === "string"
    ? err.code
    : undefined;
}

/** upper starts a sentence with a capital. */
function upper(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * eur renders integer cents as euros with the cents, as the payment-failed
 * notice writes them: *€12,34*, *€1.234,56*.
 */
export function eur(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(Math.round(cents));
  const whole = Math.floor(abs / 100)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const rest = (abs % 100).toString().padStart(2, "0");
  return `${sign}€${whole},${rest}`;
}

/** rate renders basis points as a percentage: 2100 is *21 %*, 550 is *5,5 %*. */
export function rate(bp: number): string {
  const whole = Math.floor(bp / 100);
  const frac = bp % 100;
  if (frac === 0) {
    return `${String(whole)} %`;
  }
  return `${String(whole)},${String(frac).padStart(2, "0").replace(/0$/, "")} %`;
}

/** vatOf is the VAT on a net amount at a rate, round half up, as the service counts it. */
export function vatOf(net: number, bp: number): number {
  return Math.floor((net * bp + 5000) / 10000);
}

/**
 * priceWords is a monthly net price as a card shows it: *€59,29 a month incl.
 * 21 % VAT* where the buyer's details attract VAT, *€49,00 a month excl. VAT*
 * otherwise — before any details, and where VAT is reverse-charged or the
 * buyer is outside the EU.
 */
export function priceWords(net: number, bp: number | undefined): string {
  if (bp !== undefined && bp > 0) {
    return `${eur(net + vatOf(net, bp))} a month incl. ${rate(bp)} VAT`;
  }
  return `${eur(net)} a month excl. VAT`;
}

/**
 * amountWords is an amount with its VAT, as a receipt shows it: *€59,29 incl.
 * €10,29 VAT*, *€49,00, VAT reverse-charged*, *€49,00, no VAT*.
 */
export function amountWords(a: Amount): string {
  if (a.vat_cents > 0) {
    return `${eur(a.gross_cents)} incl. ${eur(a.vat_cents)} VAT`;
  }
  if (a.treatment === Treatment.reverseCharge) {
    return `${eur(a.gross_cents)}, VAT reverse-charged`;
  }
  if (a.treatment === Treatment.export) {
    return `${eur(a.gross_cents)}, no VAT`;
  }
  return eur(a.gross_cents);
}

/** size renders bytes in whole units where they are whole: *32 GiB*, *1.5 TiB*. */
function size(n: number): string {
  const units = ["B", "KiB", "MiB", "GiB", "TiB"];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  const s = Number.isInteger(v) ? String(v) : v.toFixed(1);
  return `${s} ${units[i] ?? ""}`;
}

/** NO_BOUND is the registry's value for a limit with no bound. */
const NO_BOUND = -1;

/**
 * limitWords is a limit's value in the registry's words for people: *10
 * workspaces*, *16 virtual CPUs*, *32 GiB of memory*, *100 GiB of storage*,
 * *50 invites a day*, *no limit on workloads*.
 */
export function limitWords(name: string, v: number): string {
  const none = (what: string) => `no limit on ${what}`;
  switch (name) {
    case Limit.workspaces:
      return v === NO_BOUND ? none("workspaces") : plural(v, "workspace");
    case Limit.workloads:
      return v === NO_BOUND ? none("workloads") : plural(v, "workload");
    case Limit.vcpus:
      return v === NO_BOUND ? none("virtual CPUs") : plural(v, "virtual CPU");
    case Limit.memory:
      return v === NO_BOUND ? none("memory") : `${size(v)} of memory`;
    case Limit.storage:
      return v === NO_BOUND ? none("storage") : `${size(v)} of storage`;
    case Limit.invites:
      return v === NO_BOUND ? none("invites") : `${plural(v, "invite")} a day`;
  }
  return v === NO_BOUND ? none(name) : `${String(v)} ${name}`;
}

/** grants is what a plan grants, features first, in the registry's words for people. */
export function grants(p: Plan): string[] {
  const out = p.features.map((f) => FEATURE_WORDS[f] ?? f);
  for (const [name, v] of Object.entries(p.limits)) {
    out.push(limitWords(name, v));
  }
  return out;
}

/** unitWords is what one unit of an add-on adds: *+1 workspace*, *+2 virtual CPUs*, or the feature it grants. */
export function unitWords(a: AddOn): string {
  if (a.feature !== undefined && a.feature !== "") {
    return FEATURE_WORDS[a.feature] ?? a.feature;
  }
  if (a.limit !== undefined && a.limit !== "") {
    return `+${limitWords(a.limit, a.amount ?? 0)}`;
  }
  return "";
}

/** isSwitch reports whether an add-on is drawn as a switch: one unit grants a feature. */
export function isSwitch(a: AddOn): boolean {
  return a.feature !== undefined && a.feature !== "";
}

/** offered reports whether an add-on is offered on a plan: on every plan where it names none. */
export function offered(a: AddOn, plan: string): boolean {
  return a.plans === undefined || a.plans.length === 0 || a.plans.includes(plan);
}

/** onlyWith is the line of an add-on the catalogue offers only on some plans: *with Business*. */
export function onlyWith(a: AddOn, c: Catalogue): string | undefined {
  if (a.plans === undefined || a.plans.length === 0) {
    return undefined;
  }
  const names = a.plans.map((n) => c.plans.find((p) => p.name === n)?.display_name ?? n);
  return `with ${names.join(" or ")}`;
}

/** Selection is what the page holds selected: a plan and the add-ons' quantities. */
export interface Selection {
  plan: string;
  add_ons: Record<string, number>;
}

/** selectionOf is what the subscription has, the page's selection before a person changes it; nothing on the trial. */
export function selectionOf(sub: Subscription | undefined): Selection {
  if (sub === undefined || sub.state === State.ended) {
    return { plan: "", add_ons: {} };
  }
  return { plan: sub.plan, add_ons: { ...sub.add_ons } };
}

/** changeOf is the change a selection asks for: the add-ons offered on its plan, with a quantity above zero. */
export function changeOf(sel: Selection, c: Catalogue): Change {
  const add: Record<string, number> = {};
  for (const a of c.add_ons) {
    const q = sel.add_ons[a.name] ?? 0;
    if (q > 0 && offered(a, sel.plan)) {
      add[a.name] = q;
    }
  }
  return Object.keys(add).length > 0 ? { plan: sel.plan, add_ons: add } : { plan: sel.plan };
}

/** differs reports whether a selection asks for something other than what the subscription has. */
export function differs(sel: Selection, sub: Subscription | undefined, c: Catalogue): boolean {
  if (sel.plan === "") {
    return false;
  }
  const want = changeOf(sel, c);
  const have = selectionOf(sub);
  if (want.plan !== have.plan) {
    return true;
  }
  const names = new Set([...Object.keys(want.add_ons ?? {}), ...Object.keys(have.add_ons)]);
  for (const n of names) {
    if ((want.add_ons?.[n] ?? 0) !== (have.add_ons[n] ?? 0)) {
      return true;
    }
  }
  return false;
}

/** planName is a plan's display name from the catalogue, or its name. */
export function planName(c: Catalogue | undefined, name: string): string {
  return c?.plans.find((p) => p.name === name)?.display_name ?? name;
}

/** failedCharge is the charge *Try again* retries, among the charges the page read. */
export function failedCharge(r: SubscriptionReply, charges: readonly Charge[]): Charge | undefined {
  return r.failed_charge === undefined ? undefined : charges.find((ch) => ch.id === r.failed_charge);
}

/** Said is the tab's line, and whether it carries *Undo*. */
export interface Said {
  text: string;
  undo: boolean;
}

/**
 * tabLine is the line at the top of the tab: *Acme is on Business — €… a
 * month, next charge 1 December*; *Acme is on the trial — 12 days left;
 * choose a plan to keep going*; *Acme's payment of €… on 1 November failed —
 * update your payment method or try again by 15 November*; *Acme's plan ends
 * on 1 December* with *Undo*.
 */
export function tabLine(
  own: string,
  r: SubscriptionReply,
  c: Catalogue | undefined,
  failed: Charge | undefined,
  now: number = Date.now(),
): Said {
  const sub = r.subscription;
  if (sub === undefined || sub.state === State.ended) {
    if (sub?.ended !== undefined) {
      return {
        text: `${own}'s plan ended on ${dayOf(sub.ended, now)}; choose a plan to keep going`,
        undo: false,
      };
    }
    if (r.trial_ends === undefined) {
      return { text: `${own} is on the trial; choose a plan to keep going`, undo: false };
    }
    const n = daysLeft(r.trial_ends, now);
    return {
      text:
        n === 0
          ? `${own} is on the trial — it ends today; choose a plan to keep going`
          : `${own} is on the trial — ${plural(n, "day")} left; choose a plan to keep going`,
      undo: false,
    };
  }
  switch (sub.state) {
    case State.pastDue: {
      const of = failed !== undefined ? ` of ${eur(failed.amount.gross_cents)}` : "";
      const on = failed !== undefined ? ` on ${dayOf(failed.created, now)}` : "";
      const by = r.retry_by !== undefined ? ` by ${dayOf(r.retry_by, now)}` : "";
      return {
        text: `${own}'s payment${of}${on} failed — update your payment method or try again${by}`,
        undo: false,
      };
    }
    case State.ending:
      return { text: `${own}'s plan ends on ${dayOf(sub.cycle.end, now)}`, undo: true };
  }
  const name = planName(c, sub.plan);
  const nc = sub.next_charge;
  return {
    text:
      nc !== undefined
        ? `${own} is on ${name} — ${eur(nc.amount.gross_cents)} a month, next charge ${dayOf(nc.at, now)}`
        : `${own} is on ${name}`,
    undo: false,
  };
}

/** nextLine is a downward change waiting for the cycle's end: *From 1 December, Acme is on Starter*. */
export function nextLine(
  own: string,
  sub: Subscription | undefined,
  c: Catalogue | undefined,
  now = Date.now(),
) {
  const next = sub?.next;
  if (sub === undefined || next === undefined || next.plan === "" || sub.state === State.ending) {
    return undefined;
  }
  return `From ${dayOf(sub.cycle.end, now)}, ${own} is on ${planName(c, next.plan)}`;
}

/** cancelLine is *Cancel*'s line: what the cancel keeps and when it ends. */
export function cancelLine(own: string, sub: Subscription, now: number = Date.now()): string {
  return `${own} keeps its plan until ${dayOf(sub.cycle.end, now)}, then goes back to the trial's limits; nothing is deleted.`;
}

/** childLine is all a child's tab says. */
export function childLine(root: string): string {
  return `${root}'s plan covers this workspace — billing is on ${root}'s Settings`;
}

/**
 * barLine is the bar's words while a selection differs: *Charged now: €…
 * (incl. €… VAT) · From 1 December: €… a month* for an upward change, *From
 * 1 December: €… a month* for a downward one.
 */
export function barLine(p: PreviewReply, now: number = Date.now()): string {
  const from = `From ${dayOf(p.from, now)}: ${eur(p.monthly.gross_cents)} a month`;
  if (p.direction === Direction.up && p.now !== undefined) {
    const vat = p.now.vat_cents > 0 ? ` (incl. ${eur(p.now.vat_cents)} VAT)` : "";
    return `Charged now: ${eur(p.now.gross_cents)}${vat} · ${from}`;
  }
  return from;
}

/** lineWords is one line of what changes: *Business — €199,00 a month*, *Extra workspace × 2 — €18,00 a month*. */
export function lineWords(l: Line): string {
  const q = l.quantity > 1 ? ` × ${String(l.quantity)}` : "";
  return `${l.display_name}${q} — ${eur(l.net_cents)} a month`;
}

/** appliedLine is what the tab says after an immediate change: *Acme is on Business*, or when a downward one applies. */
export function appliedLine(own: string, p: PreviewReply, name: string, now: number = Date.now()): string {
  return p.direction === Direction.down
    ? `From ${dayOf(p.from, now)}, ${own} is on ${name}`
    : `${own} is on ${name}`;
}

/**
 * returnedLine is what the tab says back from the payment page, from the
 * newest charge: *Payment received — Acme is on Business*; *Payment pending —
 * Acme is on Business while the bank confirms*; a payment that did not go
 * through changed nothing.
 */
export function returnedLine(
  own: string,
  name: string,
  newest: Charge | undefined,
): { ok: boolean; text: string } {
  switch (newest?.status) {
    case ChargeStatus.paid:
      return { ok: true, text: `Payment received — ${own} is on ${name}` };
    case ChargeStatus.failed:
      return { ok: false, text: "The payment did not go through; nothing was changed" };
  }
  return { ok: true, text: `Payment pending — ${own} is on ${name} while the bank confirms` };
}

/** statusWords is a receipt's status as the list says it: *paid*, *pending*, *failed*, *refunded*, *charged back*. */
export function statusWords(status: string): string {
  switch (status) {
    case ChargeStatus.open:
    case ChargeStatus.pending:
      return "pending";
    case ChargeStatus.chargedBack:
      return "charged back";
  }
  return status;
}

/** statusTone colours a receipt's status. */
export function statusTone(status: string): "ok" | "bad" | "neutral" | "info" {
  switch (status) {
    case ChargeStatus.paid:
      return "ok";
    case ChargeStatus.failed:
    case ChargeStatus.chargedBack:
      return "bad";
    case ChargeStatus.open:
    case ChargeStatus.pending:
      return "info";
  }
  return "neutral";
}

/** RECEIPTS_LINE is the line above the receipts. */
export const RECEIPTS_LINE = "Your invoice for each charge is sent by email by Impire";

/** EU are the member states' codes, where a buyer outside the seller's country needs a VAT number. */
export const EU: readonly string[] = [
  "AT",
  "BE",
  "BG",
  "CY",
  "CZ",
  "DE",
  "DK",
  "EE",
  "ES",
  "FI",
  "FR",
  "GR",
  "HR",
  "HU",
  "IE",
  "IT",
  "LT",
  "LU",
  "LV",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SE",
  "SI",
  "SK",
];

/** SELLER_COUNTRY is where Impire sells from: the design's *outside Belgium in the EU*. */
export const SELLER_COUNTRY = "BE";

/** vatNeeded reports whether a buyer in this country needs a VAT number to buy a plan: in the EU, outside Belgium. */
export function vatNeeded(country: string): boolean {
  const c = country.trim().toUpperCase();
  return c !== SELLER_COUNTRY && EU.includes(c);
}

/** VAT_NEEDED is what the details say to an EU buyer outside Belgium. */
export const VAT_NEEDED = "A VAT number is needed to buy a plan.";

/** viesLine is the VAT register's answer in words: *VAT number confirmed: Acme NV, Antwerp*; nothing without a number. */
export function viesLine(d: Details | undefined): string | undefined {
  if (d === undefined || d.vat_number === undefined || d.vat_number === "") {
    return undefined;
  }
  if (d.vies === undefined) {
    return undefined;
  }
  if (!d.vies.valid) {
    return NOT_REGISTERED;
  }
  const name = d.vies.name !== undefined && d.vies.name !== "" ? d.vies.name : d.legal_name;
  return `VAT number confirmed: ${name}, ${d.address.city}`;
}

/** The VAT register's refusals, in the design's words. */
export const NOT_REGISTERED = "This VAT number is not registered";
export const REGISTER_AWAY = "The VAT register cannot be reached; try again in a while";

/**
 * billingRefusal is a refused billing verb in one sentence, or nothing for a
 * refusal that is not one of the family's: `root` names the workspace a
 * not-a-root refusal points at.
 */
export function billingRefusal(err: unknown, root?: string): string | undefined {
  switch (codeOf(err)) {
    case Code.notARoot:
      return `${upper(`billing is on ${root !== undefined && root !== "" ? root : "the top workspace"}'s Settings`)}.`;
    case Code.previewStale:
      return "Prices or dates changed while you were deciding; here is the current amount.";
    case Code.pastDue:
      return "Settle the failed payment first.";
    case Code.providerUnavailable:
      return "Our payment provider cannot be reached right now; nothing was changed.";
    case Code.vatInvalid:
      return `${NOT_REGISTERED}.`;
    case Code.vatRequired:
      return VAT_NEEDED;
    case Code.viesUnavailable:
      return `${REGISTER_AWAY}.`;
    case Code.detailsRequired:
      return "Your billing details are needed first.";
  }
  return undefined;
}

/**
 * homeLine is Home's line while billing needs a root's admins: *Acme's
 * payment of €… failed — update your payment method by 15 November*; *Acme's
 * trial ends in 12 days — choose a plan*. Nothing while a plan is simply
 * active.
 */
export function homeLine(
  own: string,
  r: SubscriptionReply,
  failed: Charge | undefined,
  now: number = Date.now(),
): string | undefined {
  const sub = r.subscription;
  if (sub?.state === State.pastDue) {
    const of = failed !== undefined ? ` of ${eur(failed.amount.gross_cents)}` : "";
    const by = r.retry_by !== undefined ? ` by ${dayOf(r.retry_by, now)}` : "";
    return `${own}'s payment${of} failed — update your payment method${by}`;
  }
  if ((sub === undefined || sub.state === State.ended) && r.trial_ends !== undefined) {
    const n = daysLeft(r.trial_ends, now);
    return n === 0
      ? `${own}'s trial ends today — choose a plan`
      : `${own}'s trial ends in ${plural(n, "day")} — choose a plan`;
  }
  return undefined;
}

/** cleaned is what the form sends: trimmed, the country and the VAT number upper case, the empty optional parts left out. */
export function cleaned(d: DetailsPutRequest): DetailsPutRequest {
  const out: DetailsPutRequest = {
    legal_name: d.legal_name.trim(),
    email: d.email.trim(),
    address: {
      line1: d.address.line1.trim(),
      postal_code: d.address.postal_code.trim(),
      city: d.address.city.trim(),
    },
    country: d.country.trim().toUpperCase(),
  };
  const line2 = d.address.line2?.trim() ?? "";
  if (line2 !== "") {
    out.address.line2 = line2;
  }
  const vat = (d.vat_number ?? "").replace(/\s/g, "").toUpperCase();
  if (vat !== "") {
    out.vat_number = vat;
  }
  return out;
}

/** complete reports whether the details hold what the service requires. */
export function complete(d: DetailsPutRequest): boolean {
  return (
    d.legal_name !== "" &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email) &&
    d.address.line1 !== "" &&
    d.address.postal_code !== "" &&
    d.address.city !== "" &&
    /^[A-Z]{2}$/.test(d.country)
  );
}
